import type { AccessRole, Perm } from "../access/api";
import { NEWS, NewsItem, Topic, TOPICS, WHOLE_TOUR } from "./content";

/** What decides a person's Help: their switches and role, and whether the account has a team at all. Never a role name as the test (the volunteer is the one exception: a volunteer holds no switches). */
export type Who = { permissions: readonly Perm[]; role: AccessRole | null; teamAllowed: boolean };

const holdsAny = (who: Who, list?: readonly Perm[]) => !!list && list.some((p) => who.permissions.includes(p));

export const topicVisible = (t: Topic, who: Who): boolean => {
  const volunteer = who.role === "volunteer";
  if (t.volunteerOnly) return volunteer;
  if (volunteer) return false;
  if (t.needs && !holdsAny(who, t.needs)) return false;
  if (t.hiddenWith && holdsAny(who, t.hiddenWith)) return false;
  if (t.needsTeam && !who.teamAllowed) return false;
  return true;
};

/** The topics this person may see, in the list's order. Hidden, never greyed (H3.2). */
export const topicsFor = (who: Who): Topic[] => TOPICS.filter((t) => topicVisible(t, who));
export const tourVisible = (who: Who): boolean => topicVisible(WHOLE_TOUR, who);
export const topicById = (id: string, who: Who): Topic | undefined => (id === WHOLE_TOUR.id ? (tourVisible(who) ? WHOLE_TOUR : undefined) : topicsFor(who).find((t) => t.id === id));

export const newsVisible = (n: NewsItem, who: Who): boolean => {
  if (who.role === "owner" || who.role === "manager") return true;
  if (who.role === "volunteer") return !!n.volunteer;
  return holdsAny(who, n.needs);
};

/** Newest first. */
export const newsFor = (who: Who): NewsItem[] => NEWS.filter((n) => newsVisible(n, who)).sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
